import React from 'react';

export interface BristolStoolType {
  type: number;
  name: string;
  category: 'Constipation' | 'Normal' | 'Loose / Diarrhea';
  description: string;
}

export const BRISTOL_TYPES: BristolStoolType[] = [
  { type: 1, name: 'Type 1', category: 'Constipation', description: 'Separate hard lumps (severe constipation)' },
  { type: 2, name: 'Type 2', category: 'Constipation', description: 'Lumpy sausage-shaped (mild constipation)' },
  { type: 3, name: 'Type 3', category: 'Normal', description: 'Sausage with surface cracks (normal)' },
  { type: 4, name: 'Type 4', category: 'Normal', description: 'Smooth, soft snake (ideal healthy gut)' },
  { type: 5, name: 'Type 5', category: 'Loose / Diarrhea', description: 'Soft blobs with clear edges (fast transit)' },
  { type: 6, name: 'Type 6', category: 'Loose / Diarrhea', description: 'Mushy, fluffy pieces (mild diarrhea/reaction)' },
  { type: 7, name: 'Type 7', category: 'Loose / Diarrhea', description: 'Watery, no solid pieces (severe diarrhea)' },
];

interface BristolStoolPickerProps {
  value: number | null | undefined;
  onChange: (val: number | null) => void;
}

export const BristolStoolPicker: React.FC<BristolStoolPickerProps> = ({ value, onChange }) => {
  const selectedType = BRISTOL_TYPES.find((b) => b.type === value);

  return (
    <div className="space-y-2 text-left">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center space-x-1.5">
          <span>Bristol Stool Scale (Bowel Movement)</span>
        </label>
        {value ? (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-[10px] text-slate-400 hover:text-rose-500"
          >
            Clear Stool Entry
          </button>
        ) : (
          <span className="text-[11px] text-slate-400 italic">Optional</span>
        )}
      </div>

      {/* Segmented 1-7 Selector */}
      <div className="grid grid-cols-7 gap-1 p-1 bg-slate-100 dark:bg-[#0e1017] rounded-xl border border-slate-200 dark:border-slate-800">
        {BRISTOL_TYPES.map((item) => {
          const isSelected = value === item.type;
          let color = 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800';

          if (isSelected) {
            if (item.type <= 2) {
              color = 'bg-amber-500 text-white font-bold shadow-sm';
            } else if (item.type <= 4) {
              color = 'bg-emerald-500 text-black font-bold shadow-sm';
            } else {
              color = 'bg-rose-500 text-white font-bold shadow-sm';
            }
          }

          return (
            <button
              key={item.type}
              type="button"
              onClick={() => onChange(isSelected ? null : item.type)}
              className={`py-2 rounded-lg text-xs font-semibold flex flex-col items-center justify-center transition-all active:scale-95 ${color}`}
            >
              <span className="text-[11px]">T{item.type}</span>
            </button>
          );
        })}
      </div>

      {/* Description of active selection */}
      {selectedType ? (
        <div className={`p-2.5 rounded-xl border text-xs leading-tight flex items-center justify-between ${
          selectedType.type <= 2
            ? 'bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200'
            : selectedType.type <= 4
            ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200'
            : 'bg-rose-50 dark:bg-rose-950/40 border-rose-300 dark:border-rose-800 text-rose-800 dark:text-rose-200'
        }`}>
          <div>
            <span className="font-bold">{selectedType.name} ({selectedType.category}):</span>{' '}
            <span>{selectedType.description}</span>
          </div>
        </div>
      ) : (
        <p className="text-[11px] text-slate-500 dark:text-slate-400 italic">
          Select 1–7 if you had a bowel movement. Helps identify foods triggering constipation or loose stools.
        </p>
      )}
    </div>
  );
};
